import type { ComponentType, ReactNode } from 'react';
import * as React from 'react';
import { createContext, useCallback, useMemo, useState } from 'react';

import type {
  ChangedResponse,
  Response,
} from 'bundles/compound-assessments/components/local-state/changed-response/types';

export type GetChangedResponse = (id: string) => ChangedResponse;

export type UpdateChangedResponse = (id: string, changedResponse: Response | null | undefined) => void;

export type ChangedResponseContextValue = {
  getChangedResponse: GetChangedResponse;
  updateChangedResponse: UpdateChangedResponse;
};

export const ChangedResponseContext = createContext<ChangedResponseContextValue>({
  getChangedResponse: (id: string) => ({ id, response: null, __typename: 'LocalChangedResponse' }),
  updateChangedResponse: () => undefined,
});

export const ChangedResponseContextProvider = ({ children }: { children: ReactNode }) => {
  const [responses, setResponses] = useState<Map<string, ChangedResponse>>(() => new Map());

  const getChangedResponse = useCallback<GetChangedResponse>(
    (id) => responses.get(id) ?? { id, response: null, __typename: 'LocalChangedResponse' },
    [responses]
  );

  const updateChangedResponse = useCallback<ChangedResponseContextValue['updateChangedResponse']>(
    (id, changedResponse) => {
      setResponses((prevResponses) => {
        const nextResponses = new Map(prevResponses);
        nextResponses.set(id, { id, response: changedResponse ?? null, __typename: 'LocalChangedResponse' });
        return nextResponses;
      });
    },
    []
  );

  const contextValue = useMemo<ChangedResponseContextValue>(
    () => ({
      getChangedResponse,
      updateChangedResponse,
    }),
    [getChangedResponse, updateChangedResponse]
  );

  return <ChangedResponseContext.Provider value={contextValue}>{children}</ChangedResponseContext.Provider>;
};

export const withChangedResponseContext = <BaseProps extends {}>(BaseComponent: ComponentType<BaseProps>) => {
  return (props: BaseProps) => (
    <ChangedResponseContextProvider>
      <BaseComponent {...props} />
    </ChangedResponseContextProvider>
  );
};
